import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  computed,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  DataTableComponent,
  TableColumn,
} from '@shared/components/data-table/data-table.component';
import { Barcode } from '../../models/sku.interface';

@Component({
  selector: 'app-barcode-table',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AppButtonComponent,
    DataTableComponent,
  ],
  templateUrl: './barcode-table.component.html',
  styleUrls: ['./barcode-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarcodeTableComponent implements OnChanges {
  private readonly translateService = inject(TranslateService);

  @Input() barcodeList: Barcode[] = [];
  @Input() barcodes: Barcode[] = [];
  @Input() selectedBarcodes: Barcode[] = [];
  @Input() showTitle = true;
  @Input() title = 'sku_management.barcode.title';
  @Input() viewMode = false;

  @Output() addBarcode = new EventEmitter<void>();
  @Output() editBarcode = new EventEmitter<Barcode>();
  @Output() deleteBarcode = new EventEmitter<Barcode>();
  @Output() deleteSelectedBarcodes = new EventEmitter<void>();
  @Output() selectionChange = new EventEmitter<Barcode[]>();

  // Use signal to track barcode list changes
  private barcodeListSignal = signal<Barcode[]>([]);
  private barcodesSignal = signal<Barcode[]>([]);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['barcodeList']) {
      this.barcodeListSignal.set(this.barcodeList || []);
    }
    if (changes['barcodes']) {
      this.barcodesSignal.set(this.barcodes || []);
    }
  }

  displayBarcodes = computed<Barcode[]>(() => {
    return this.barcodeListSignal().length > 0
      ? this.barcodeListSignal()
      : this.barcodesSignal();
  });

  barcodeTableColumns = computed<TableColumn[]>(() => {
    const columns: TableColumn[] = [
      {
        label: this.translateService.instant('sku_management.barcode.table.barcode'),
        prop: 'barcodeValue',
        width: 200,
        align: 'left',
      },
      {
        label: this.translateService.instant('sku_management.barcode.table.barcode_type'),
        prop: 'barcodeType',
        width: 150,
        align: 'left',
      },
    ];

    // Only show actions column if not in view mode
    if (!this.viewMode) {
      columns.push({
        label: this.translateService.instant('sku_management.barcode.table.actions'),
        prop: 'actions',
        type: 'actions',
        width: 120,
        align: 'center',
      });
    }

    return columns;
  });

  onAddBarcode(): void {
    this.addBarcode.emit();
  }

  onEditBarcode(barcode: Barcode): void {
    this.editBarcode.emit(barcode);
  }

  onDeleteBarcode(barcode: Barcode): void {
    this.deleteBarcode.emit(barcode);
  }

  onDeleteSelectedBarcodes(): void {
    this.deleteSelectedBarcodes.emit();
  }

  onSelectionChange(selected: Barcode[]): void {
    this.selectionChange.emit(selected);
  }
}

